import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { useToast } from "@/components/ui/UseToast";
import { DotsVerticalIcon } from "@radix-ui/react-icons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { useDeleteEventMutation, useUpdateEventStatusMutation } from "@/services/EventApi";
import { Event, EventStatus } from "@/app/types/event";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface DataTableRowActionsProps {
  data: Event;
  refetch: () => void;
  handleEdit: (data: Event) => void;
  access: string;
}

export function DataTableRowActions({
  data,
  handleEdit,
  access,
}: DataTableRowActionsProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isActivateAlertOpen, setIsActivateAlertOpen] = useState(false);
  const [isInactivateAlertOpen, setIsInactivateAlertOpen] = useState(false);

  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();
  const [updateEventStatus, { isLoading: isActivating }] = useUpdateEventStatusMutation();

  const handleDelete = async () => {
    try {
      await deleteEvent(data.event_id).unwrap();
      toast({
        title: "Success",
        description: "Event deleted successfully",
      });
      setIsAlertOpen(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.data?.message || "Failed to delete event",
      });
    }
  };

  const handleActivate = async () => {
    try {
      await updateEventStatus({ event_id: data.event_id, status: EventStatus.ACTIVE }).unwrap();
      toast({
        title: "Success",
        description: "Event activated successfully",
      });
      setIsActivateAlertOpen(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.data?.message || "Failed to activate event",
      });
    }
  };

  const handleInactivate = async () => {
    try {
      await updateEventStatus({ event_id: data.event_id, status: EventStatus.INACTIVE }).unwrap();
      toast({
        title: "Success",
        description: "Event inactivated successfully",
      });
      setIsInactivateAlertOpen(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.data?.message || "Failed to inactivate event",
      });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <DotsVerticalIcon className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[160px]">
          <DropdownMenuItem onClick={() => handleEdit(data)}>
            Edit
          </DropdownMenuItem>
          {data.status !== EventStatus.ACTIVE && (
            <DropdownMenuItem onClick={() => setIsActivateAlertOpen(true)}>
              Activate
            </DropdownMenuItem>
          )}
          {data.status === EventStatus.ACTIVE && (
            <DropdownMenuItem onClick={() => setIsInactivateAlertOpen(true)}>
              Inactivate
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => navigate(`/admin/events/${data.event_id}/tags`)}>
            Manage Tags
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(`/admin/events/${data.event_id}/tickets`)}>
            Manage Tickets
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-red-600"
            onClick={() => setIsAlertOpen(true)}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              event "{data.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} className="w-24">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 focus:ring-red-600"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isActivateAlertOpen} onOpenChange={setIsActivateAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate Event?</AlertDialogTitle>
            <AlertDialogDescription>
              Activating "{data.name}" will deactivate any other currently active event.
              Queue management will be enabled as per this event's configuration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="w-24" disabled={isActivating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleActivate}
              disabled={isActivating}
            >
              {isActivating ? "Activating..." : "Activate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isInactivateAlertOpen} onOpenChange={setIsInactivateAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inactivate Event?</AlertDialogTitle>
            <AlertDialogDescription>
              Inactivating "{data.name}" will stop queue management for this event.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="w-24" disabled={isActivating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleInactivate}
              disabled={isActivating}
            >
              {isActivating ? "Inactivating..." : "Inactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
